'use client'

import React, { useContext } from 'react'
import { Session } from 'next-auth'
import { usePathname } from 'next/navigation'
import { Sidebar as _Sidebar } from '@tribex/stackup-react-ui'

const Sidebar = _Sidebar as React.ComponentType<
  React.ComponentProps<typeof _Sidebar> & {
    portalItems?: Array<{ label: string; url: string }>
  }
>
import { oidcSignOut } from '@/lib/auth/services'
import { NavConfig } from '@/types/NavConfig'
import NavBarContext from '@/app/web/context/NavContext'

const PORTAL_NAV: Record<string, { title: string; url: string }> = {
  admin: { title: 'Admin Portal', url: '/admin/bug-bounty' },
  manager: { title: 'Manager Portal', url: '/manager/bug-bounty' },
  organizer: { title: 'Manager Portal', url: '/manager/bug-bounty' },
  reviewer: { title: 'Reviewer Portal', url: '/api/reviewer/auth-bridge' },
}

const ROLE_PRIORITY = ['admin', 'organizer', 'manager', 'reviewer']

interface AppSideNavProps {
  session?: Session | null
  navConfig?: NavConfig | null
  signOutEndpoint?: string | null
  roles?: string[]
}

interface PortalEntry {
  title: string
  url: string
}

const PortalSwitchButton: React.FC<{ portalEntry: PortalEntry }> = ({
  portalEntry,
}) => {
  const handleClick = () => {
    if (portalEntry.url.startsWith('http')) {
      window.location.href = portalEntry.url
    } else {
      window.location.href = portalEntry.url
    }
  }

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={handleClick}
        aria-label={portalEntry.title}
        className="w-10 h-10 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 3M21 7.5H7.5"
          />
        </svg>
      </button>
      <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-50">
        {portalEntry.title}
      </span>
    </div>
  )
}

const AppSideNav: React.FC<AppSideNavProps> = ({
  session,
  navConfig,
  signOutEndpoint,
  roles = [],
}) => {
  const { isMobile, setIsMobile } = useContext(NavBarContext)
  const pathname = usePathname()

  if (!navConfig || !signOutEndpoint) return

  const hackathonUrl = process.env.NEXT_PUBLIC_HACKATHON_URL
  const hackathonIndex = navConfig?.navItems.findIndex(
    (item) => item.title === 'Hackathon'
  )
  const isEventsActive = pathname.startsWith('/web/events')
  const isBugBountyActive = pathname.startsWith('/web/bug-bounty')

  const baseItems =
    navConfig?.navItems.map((item) => ({
      ...item,
      url: item.title === 'Hackathon' && hackathonUrl ? hackathonUrl : item.url,
      active: item.title === 'Hackathon' ? isEventsActive : false,
      showTooltip: item.title === 'Hackathon' ? !isEventsActive : true,
    })) ?? []

  // Bug Bounty nav is opt-in via env — hidden by default so it can be kept
  // out of the UI until a client is ready. Set NEXT_PUBLIC_ENABLE_BUG_BOUNTY
  // to "true" to show it.
  const isBugBountyEnabled =
    process.env.NEXT_PUBLIC_ENABLE_BUG_BOUNTY === 'true'

  const bugBountyItem = {
    title: 'Bug Bounty',
    icon: 'bounties' as const,
    url: '/web/bug-bounty',
    active: isBugBountyActive,
    showTooltip: !isBugBountyActive,
  }

  const insertIndex =
    hackathonIndex !== undefined && hackathonIndex !== -1
      ? hackathonIndex + 1
      : baseItems.length

  const normalizedRoles = roles?.map((r) => r?.toLowerCase() ?? '') ?? []
  const matchedRoleKey = ROLE_PRIORITY.find((key) =>
    normalizedRoles.includes(key)
  )
  const portalEntry = matchedRoleKey ? PORTAL_NAV[matchedRoleKey] : undefined

  const navItems = [
    ...baseItems.slice(0, insertIndex),
    ...(isBugBountyEnabled ? [bugBountyItem] : []),
    ...baseItems.slice(insertIndex),
  ]
  const passportInfo = {
    url: navConfig.passportUrl,
    active: false,
    showTooltip: false,
  }
  const settingUrl = navConfig.settingUrl

  // @tribex/stackup-react-ui's Sidebar renders `${firstName} ${lastName}`
  // with no fallback. Accounts that never set a name (e.g. created via
  // email/password-only custom StackUp registration) have both undefined,
  // which would otherwise literally render "undefined undefined".
  const sidebarUser = session?.user && {
    ...session.user,
    firstName: session.user.firstName || session.user.username || '',
    lastName: session.user.lastName || '',
  }

  return (
    <div className="relative">
      <Sidebar
        showMobileSidebar={isMobile}
        user={sidebarUser}
        passport={passportInfo}
        settingUrl={settingUrl}
        navItems={navItems}
        portalItems={
          portalEntry
            ? [{ label: portalEntry.title, url: portalEntry.url }]
            : []
        }
        onLogout={() => oidcSignOut(signOutEndpoint)}
        onHide={() => setIsMobile(false)}
      />
      {portalEntry && (
        <div className="fixed left-0 bottom-[68px] w-[60px] items-center justify-center z-50 hidden md:flex">
          <PortalSwitchButton portalEntry={portalEntry} />
        </div>
      )}
    </div>
  )
}

export default AppSideNav
